import { useState, useEffect } from "react"
import { api } from "aldoo-ra/API"
import { url } from "aldoo-ra/CMS/url"
import Typography from "aldoo-ra/Typography"
import { ChevronRightIcon } from "@heroicons/react/24/outline"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import { useHelpCenterHomeSeed } from "../ssr-seed"

const hcAPI = api("help-center")

// A featured category's card: its label and icon, with its published
// subcategories and articles (`catData`: the category's own content)
const featuredCategoryItem = (catRef, catData) => {
  const subcategories = (catData?.content?.categories || [])
    .filter((c) => c.status === "published")
    .map((c) => ({ ...c, _itemType: "category" }))
  const articles = (catData?.content?.articles || [])
    .filter((a) => a.status === "published")
    .map((a) => ({ ...a, _itemType: "article" }))
  return {
    _id: catRef._id,
    _type: "category",
    label: catRef.label || catData?.content?.name,
    slug: catRef.slug,
    icon: catRef.icon || catData?.content?.icon,
    status: catRef.status,
    children: [...subcategories, ...articles],
  }
}

// The section's cards: its published categories (`categoryData`: slug → the
// category's content), then its published articles
const featuredItems = (content, categoryData) => [
  ...(content?.categories || [])
    .filter((c) => c.status === "published")
    .map((catRef) => featuredCategoryItem(catRef, categoryData?.[catRef.slug])),
  ...(content?.articles || [])
    .filter((a) => a.status === "published")
    .map((a) => ({ ...a, _type: "article" })),
]

const FeaturedCategories = () => {
  const texts = TextBundle({ id: "help-center" })
  const [currentLanguage] = useState(() => {
    try {
      const stored = localStorage.getItem("current-site-language")
      return stored ? stored.replace(/"/g, "") : "en"
    } catch { return "en" }
  })

  // rendered by the server: start from its copy and skip the fetch
  const seed = useHelpCenterHomeSeed(currentLanguage)
  const [items, setItems] = useState(() =>
    seed ? featuredItems(seed.featured?.content, seed.featuredCategories) : []
  )
  const [sectionName, setSectionName] = useState(seed?.featured?.content?.name || "Featured")
  const [isLoading, setIsLoading] = useState(!seed)

  useEffect(() => {
    if (seed) return
    const fetchItems = async () => {
      try {
        const response = await hcAPI({
          get_content: { contentURL: "/category/featured", language: currentLanguage },
        })

        const content = response?.content
        if (!content) { setItems([]); return }

        setSectionName(content.name || "Featured")

        // Categories — fetch each one's articles
        const publishedCategoryRefs = (content.categories || []).filter(
          (c) => c.status === "published"
        )

        const categoryData = {}
        await Promise.all(
          publishedCategoryRefs.map(async (catRef) => {
            try {
              categoryData[catRef.slug] = await hcAPI({
                get_content: { contentURL: `/category/${catRef.slug}`, language: currentLanguage },
              })
            } catch {
              // listed without its articles
            }
          })
        )

        setItems(featuredItems(content, categoryData))
      } catch {
        setItems([])
      } finally {
        setIsLoading(false)
      }
    }
    fetchItems()
  }, [currentLanguage])

  const handleCategoryClick = (slug) => {
    if (window.loadHelpCenterContent) {
      window.loadHelpCenterContent("category", slug, null)
    } else {
      window.location.href = `/help-center/category/${slug}`
    }
  }

  const handleArticleClick = (e, categorySlug, articleSlug) => {
    e.preventDefault()
    if (window.loadHelpCenterContent) {
      window.loadHelpCenterContent("article", articleSlug, categorySlug)
    } else {
      const path = categorySlug
        ? `/help-center/category/${categorySlug}/article/${articleSlug}`
        : `/help-center/article/${articleSlug}`
      window.location.href = path
    }
  }

  const handleDirectArticleClick = (slug) => {
    if (window.loadHelpCenterContent) {
      window.loadHelpCenterContent("article", slug, null)
    } else {
      window.location.href = `/help-center/article/${slug}`
    }
  }

  if (isLoading) return (
    <div className="py-8 text-center">
      <Typography variant="txt1_dim">
        {texts?.loading_featured_cat || "Loading featured categories..."}
      </Typography>
    </div>
  )

  if (!items.length) return null

  return (
    <div className="mt-14">
      <Typography 
        component="h2"
        variant="h3" 
        className="text-center md:text-left mb-6 md:mb-11"
      >
        {sectionName}
      </Typography>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 md:gap-7">
        {items.map((item) => item._type === "category" ? (
          <div
            key={item._id}
            className="border border-bordered bg-paper rounded-[30px] overflow-hidden transition-shadow px-[26px]"
          >
            <a
              href={`/help-center/category/${item.slug}`}
              className="h-[100px] flex items-center cursor-pointer border-b border-bordered no-underline py-6"
              onClick={(e) => { e.preventDefault(); handleCategoryClick(item.slug) }}
              aria-label={`View ${item.label} category`}
            >
              {item.icon && (
                <div className="mr-[10px]">
                  <img src={url(item.icon)} alt={`${item.label} icon`} className="w-[50px] h-[50px] object-cover" />
                </div>
              )}

              <Typography 
                component="h3"
                variant="h4" 
                className="text-text hover:text-text 2xl:!text-[clamp(24px,1.2vw,26px)] my-0"
              >
                {item.label}
              </Typography>

              <div className="w-[30px] h-[30px] flex items-center justify-center bg-primary_lt rounded-full ml-auto">
                <ChevronRightIcon className="w-[18px] h-[18px] text-text_inv stroke-[3]" />
              </div>
            </a>

            <div className="py-4">
              {item.children?.slice(0, 3).map((child) => child._itemType === "category" ? (
                <a
                  key={child._id}
                  href={`/help-center/category/${item.slug}/${child.slug}`}
                  className="h-[68px] flex items-center gap-4 hover:bg-gradient_showcase_card rounded-[20px] cursor-pointer no-underline px-2"
                  onClick={(e) => { e.preventDefault(); handleCategoryClick(child.slug) }}
                  aria-label={`View ${child.label} category`}
                >
                  <Typography 
                    variant="body1_dim" 
                    className="flex-grow my-0"
                  >
                    {child.label}
                  </Typography>

                  <ChevronRightIcon className="w-[18px] h-[18px] text-text_secondary stroke-2 shrink-0" />
                </a>
              ) : (
                <a
                  key={child._id}
                  href={`/help-center/category/${item.slug}/article/${child.slug}`}
                  className="h-[68px] flex items-center gap-4 hover:bg-gradient_showcase_card rounded-[20px] cursor-pointer no-underline px-2"
                  onClick={(e) => handleArticleClick(e, item.slug, child.slug)}
                  aria-label={`Read ${child.label} article`}
                >
                  <Typography 
                    variant="body1_dim" 
                    className="flex-grow my-0"
                  >
                    {child.label}
                  </Typography>

                  <ChevronRightIcon className="w-[18px] h-[18px] text-text_secondary stroke-2 flex-shrink-0 " />
                </a>
              ))}

              {!item.children?.length && (
                <div className="px-6 py-4">
                  <Typography variant="body1_dim">
                    {texts?.no_articles_in_cat || "No articles available"}
                  </Typography>
                </div>
              )}
            </div>
          </div>
        ) : (
          <a
            key={item._id}
            href={`/help-center/article/${item.slug}`}
            className="h-[186px] flex flex-col items-center justify-center bg-paper border border-bordered hover:border-secondary rounded-[30px] p-8 cursor-pointer hover:shadow-soft_lg transition-shadow no-underline"
            onClick={(e) => { e.preventDefault(); handleDirectArticleClick(item.slug) }}
          >
            <Typography variant="h4" className="text-center my-0">{item.label}</Typography>
          </a>
        ))}
      </div>
    </div>
  )
}

if (typeof window !== "undefined") {
  window.initHelpCenterNavigation = (loadContentFunction) => {
    window.loadHelpCenterContent = loadContentFunction
  }
}

export default FeaturedCategories
